import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Spot as SpotType } from '../../types';
import { isValidCarType, isValidPerformanceType } from '../../constants/spotCategories';
import { ImageCarousel } from '../ImageCarousel/ImageCarousel';
import { useAuth } from '../../context/AuthContext';
import { useData } from '../../context/DataContext';
import { AuthRequiredModal } from '../AuthRequiredModal/AuthRequiredModal';
import { uploadUrl } from '../../api/client';
import { getCountryFlagImageUrl, getCountryName } from '../../constants/countries';
import styles from './FeedCard.module.css';

interface FeedCardProps {
  spot: SpotType;
  compact?: boolean;
}

function formatDate(iso: string) {
  const d = new Date(iso);
  const now = new Date();
  const diff = now.getTime() - d.getTime();
  if (diff < 60000) return 'Just now';
  if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
  if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
  if (diff < 604800000) return `${Math.floor(diff / 86400000)}d ago`;
  return d.toLocaleDateString();
}

function getReactionCount(spot: SpotType, field: 'likes' | 'beautiful' | 'cool' | 'sweetPhoto') {
  const countField =
    field === 'likes'
      ? spot.likeCount
      : field === 'beautiful'
        ? spot.beautifulCount
        : field === 'cool'
          ? spot.coolCount
          : spot.sweetPhotoCount;
  return countField ?? (spot[field] ?? []).length;
}

function getCommentCount(spot: SpotType) {
  return spot.commentCount ?? spot.comments.length;
}

export function FeedCard({ spot, compact }: FeedCardProps) {
  const { user: currentUser, isAuthenticated } = useAuth();
  const { getAuthor, likeSpot, unlikeSpot, beautifulSpot, unBeautifulSpot, coolSpot, unCoolSpot, sweetPhotoSpot, unSweetPhotoSpot } = useData();
  const author = spot.author ?? getAuthor(spot.authorId);
  const topBadges = author?.topBadges ?? [];
  const [showAuthModal, setShowAuthModal] = useState(false);
  const liked = currentUser && (spot.likedByCurrentUser ?? spot.likes.includes(currentUser.id));
  const beautiful = currentUser && (spot.beautifulByCurrentUser ?? (spot.beautiful ?? []).includes(currentUser.id));
  const cool = currentUser && (spot.coolByCurrentUser ?? (spot.cool ?? []).includes(currentUser.id));
  const sweetPhoto = currentUser && (spot.sweetPhotoByCurrentUser ?? (spot.sweetPhoto ?? []).includes(currentUser.id));

  const requireAuth = () => {
    if (!isAuthenticated) {
      setShowAuthModal(true);
      return true;
    }
    return false;
  };
  const handleLike = () => {
    if (requireAuth() || !currentUser) return;
    if (liked) unlikeSpot(spot.id, currentUser.id);
    else likeSpot(spot.id, currentUser.id);
  };
  const handleBeautiful = () => {
    if (requireAuth() || !currentUser) return;
    if (beautiful) unBeautifulSpot(spot.id, currentUser.id);
    else beautifulSpot(spot.id, currentUser.id);
  };
  const handleCool = () => {
    if (requireAuth() || !currentUser) return;
    if (cool) unCoolSpot(spot.id, currentUser.id);
    else coolSpot(spot.id, currentUser.id);
  };
  const handleSweetPhoto = () => {
    if (requireAuth() || !currentUser) return;
    if (sweetPhoto) unSweetPhotoSpot(spot.id, currentUser.id);
    else sweetPhotoSpot(spot.id, currentUser.id);
  };

  const carLabel = [spot.car.make, spot.car.model, spot.car.generation, spot.car.trim, spot.car.yearRange].filter(Boolean).join(' ');

  return (
    <article className={`${styles.card} ${compact ? styles.compact : ''}`.trim()}>
      <div className={styles.cardHeader}>
        <Link to={`/user/${spot.authorId}`} className={styles.author}>
          <span className={styles.authorAvatar}>
            {author?.avatarUrl ? (
              <img
                src={author.avatarUrl.startsWith('http') ? author.avatarUrl : uploadUrl(author.avatarUrl)}
                alt=""
                className={styles.authorAvatarImg}
                loading="lazy"
                decoding="async"
              />
            ) : (
              <span className={styles.authorAvatarLetter}>{author?.displayName?.charAt(0) ?? '?'}</span>
            )}
          </span>
          <div>
            <span className={styles.authorName}>{author?.displayName ?? 'Unknown'}</span>
            {topBadges.length > 0 && (
              <div className={styles.authorBadges}>
                {topBadges.slice(0, 3).map((b) => (
                  <span key={b.id} className={styles.miniBadge} data-tier={b.tier} title={b.name}>
                    {b.name}
                  </span>
                ))}
              </div>
            )}
            <span className={styles.meta}>
              {[spot.city, spot.event].filter(Boolean).join(' · ') || 'Shared a spot'}
              {' · '}
              {formatDate(spot.createdAt)}
            </span>
          </div>
        </Link>
        {currentUser && spot.authorId === currentUser.id && (
          <Link to={`/spot/${spot.id}/edit`} className={styles.editLink} onClick={(e) => e.stopPropagation()}>
            Edit
          </Link>
        )}
      </div>

      <div className={styles.imageContainer}>
        <Link to={`/spot/${spot.id}`} className={styles.imageWrap}>
          <ImageCarousel
            images={spot.images?.length ? spot.images : (spot.imageUrl ? [spot.imageUrl] : [])}
            alt={carLabel}
            wrapClassName={styles.imageWrapInner}
            imageClassName={styles.image}
            onControlClick={(e) => { e.preventDefault(); e.stopPropagation(); }}
          />
        </Link>
        {spot.countryCode && (
          <div className={styles.countryFlagRow}>
            <span className={styles.countryFlag}>
              <img
                src={getCountryFlagImageUrl(spot.countryCode)}
                alt={getCountryName(spot.countryCode) || spot.countryCode}
                title={`Spotted in ${[spot.city, getCountryName(spot.countryCode) || spot.countryCode].filter(Boolean).join(', ')}`}
                className={styles.countryFlagImg}
                loading="lazy"
                decoding="async"
              />
            </span>
          </div>
        )}
      </div>
      {spot.caption && <p className={styles.caption}>{spot.caption}</p>}
      <div className={styles.body}>
        <p className={styles.carTitle}>{carLabel}</p>
        {(isValidCarType(spot.car.bodyStyle) || isValidPerformanceType(spot.car.performanceType)) && (
          <>
            {isValidCarType(spot.car.bodyStyle) && <span className={styles.badge}>{spot.car.bodyStyle}</span>}
            {isValidPerformanceType(spot.car.performanceType) && <span className={styles.badge}>{spot.car.performanceType}</span>}
          </>
        )}
        {(spot.car.mods || spot.car.wheels || spot.car.userNotes) && (
          <p className={styles.notes}>
            {[spot.car.mods, spot.car.wheels, spot.car.userNotes].filter(Boolean).join(' · ')}
          </p>
        )}
        {spot.taggedOwnerId && (
          <p className={styles.tagged}>
            Tagged: {getAuthor(spot.taggedOwnerId)?.displayName ?? 'User'}
          </p>
        )}
      </div>

      <div className={styles.actions}>
        <button
          type="button"
          className={liked ? styles.likeActive : styles.likeBtn}
          onClick={handleLike}
          aria-pressed={!!liked}
          title="Like"
        >
          ♥ {getReactionCount(spot, 'likes')}
        </button>
        <button
          type="button"
          className={beautiful ? styles.reactActive : styles.reactBtn}
          onClick={handleBeautiful}
          aria-pressed={!!beautiful}
          title="Beautiful"
        >
          ✨ {getReactionCount(spot, 'beautiful')}
        </button>
        <button
          type="button"
          className={cool ? styles.reactActive : styles.reactBtn}
          onClick={handleCool}
          aria-pressed={!!cool}
          title="Cool"
        >
          😎 {getReactionCount(spot, 'cool')}
        </button>
        <button
          type="button"
          className={sweetPhoto ? styles.reactActive : styles.reactBtn}
          onClick={handleSweetPhoto}
          aria-pressed={!!sweetPhoto}
          title="Sweet Photo"
        >
          📷 {getReactionCount(spot, 'sweetPhoto')}
        </button>
        <Link
          to={`/spot/${spot.id}#comments`}
          className={`${styles.commentBtn} ${styles.commentLink}`}
          onClick={(e) => {
            if (requireAuth()) {
              e.preventDefault();
            }
          }}
        >
          💬 {getCommentCount(spot)}
        </Link>
        <span className={styles.views}>👁 {spot.viewCount}</span>
      </div>
      {showAuthModal && <AuthRequiredModal onClose={() => setShowAuthModal(false)} />}
    </article>
  );
}
